import {
  type EnsureQueryDataOptions,
  type FetchQueryOptions,
  type QueryClient,
  type QueryKey,
  queryOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { redirect, useRouteContext, useRouter } from "@tanstack/react-router";
import {
  ApiError,
  api,
  showCompany,
  shownCompany,
  signedOutStore,
  unwrap,
} from "@/client/lib/api";
import { takeEarly } from "@/client/lib/early-reads";
import { forgetPersisted } from "@/client/lib/persist";
import { forgetShell } from "@/client/lib/shell-boot";
import type { SessionSnapshot } from "@/features/identity/schemas";
import type { CompanyModule } from "@/shared/modules";

export type { SessionSnapshot };

export interface CompanySession extends SessionSnapshot {
  readonly userId: string;
  readonly companyId: string;
  readonly companySlug: string;
  readonly companyTimezone: string;
  readonly role: NonNullable<SessionSnapshot["role"]>;
  readonly companyModules: CompanyModule[];
}

// The company fields arrive null together, so a screen under `/$slug` gets all of them or none.
function companyOf(session: SessionSnapshot): CompanySession | null {
  const { userId, companyId, companySlug, companyTimezone, role } = session;
  if (!userId || !companyId || !companySlug || !companyTimezone || !role) {
    return null;
  }
  const companyModules = session.companyModules ?? [];
  return {
    ...session,
    userId,
    companyId,
    companySlug,
    companyTimezone,
    role,
    companyModules,
  };
}

// A module that is off leaves no trace: no link, no command, no request for its data.
export function useModuleOn(wanted: CompanyModule): boolean {
  return useRouteContext({
    from: "/_authenticated/$slug",
  }).companyModules.includes(wanted);
}

// An address inside a module that is off redirects to the company's home.
export function requireModule(
  session: CompanySession,
  wanted: CompanyModule,
): void {
  if (!session.companyModules.includes(wanted)) {
    throw redirect({ to: "/$slug", params: { slug: session.companySlug } });
  }
}

// Guards read this from the cache, so moving between screens makes no request. Signed out and
// signed in with no company are ordinary answers, not refusals.
export const sessionQuery = queryOptions({
  queryKey: ["session"],
  queryFn: async (): Promise<SessionSnapshot> =>
    (await takeEarly("session")) ?? unwrap(api.session.$get()),
  staleTime: 30_000,
});

/**
 * After a company setting the snapshot carries changes (its name, zone, logo or modules). Every
 * screen reads them from the route context, which only the guards rebuild.
 */
export function useRefreshSession(): () => Promise<void> {
  const queryClient = useQueryClient();
  const router = useRouter();
  return async () => {
    await queryClient.refetchQueries(sessionQuery);
    await router.invalidate();
  };
}

// Removed, not invalidated: `ensureQueryData` answers from any cached entry, and a stale
// signed-out snapshot would send someone who just signed in back to sign-in.
export function forgetSession(queryClient: QueryClient): void {
  queryClient.removeQueries();
  // Not awaited: memory is already empty, and a browser without IndexedDB has nothing to clear.
  void forgetPersisted().catch(() => undefined);
  forgetShell();
  // Until a guard reconciles address and session, tenant requests from this tab are refused
  // rather than scoped by whatever the session now says.
  showCompany(null);
}

// A courtesy only: a tab can miss a message, and the server refusing a stale tab keeps data right.
// One channel sends and listens, since a channel never hears its own messages.
let companyChannel: BroadcastChannel | null | undefined;

function channel(): BroadcastChannel | null {
  if (companyChannel === undefined) {
    companyChannel =
      typeof BroadcastChannel === "function"
        ? new BroadcastChannel("company")
        : null;
  }
  return companyChannel;
}

export function announceCompany(slug: string): void {
  channel()?.postMessage({ slug });
}

// A tab already showing the announced company, or showing none, has nothing to follow.
export function onCompanyAnnounced(listener: () => void): () => void {
  const heard = (event: MessageEvent<unknown>) => {
    const shown = shownCompany();
    const slug: unknown =
      typeof event.data === "object" && event.data !== null
        ? Reflect.get(event.data, "slug")
        : null;
    if (shown !== null && slug !== shown) listener();
  };
  channel()?.addEventListener("message", heard);
  return () => channel()?.removeEventListener("message", heard);
}

// Read when the switcher opens, not on every screen.
export const companiesQuery = queryOptions({
  queryKey: ["me", "companies"],
  queryFn: () => unwrap(api.me.companies.$get()).then(({ items }) => items),
});

// The cache is emptied either way: after a switch it belongs to the other company, after a
// failure nobody knows which company the server now points at.
export async function switchCompany(
  queryClient: QueryClient,
  companyId: string,
): Promise<string> {
  try {
    const { slug } = await unwrap(
      api.me["active-company"].$post({ json: { companyId } }),
    );
    announceCompany(slug);
    return slug;
  } finally {
    forgetSession(queryClient);
  }
}

// A read refused because another tab switched company follows the session instead of showing
// an error.
function followIfCompanyChanged(
  queryClient: QueryClient,
  failure: unknown,
): void {
  if (failure instanceof ApiError && failure.code === "COMPANY_CHANGED") {
    forgetSession(queryClient);
    throw redirect({ to: "/" });
  }
}

export function ensureTenantData<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
>(
  queryClient: QueryClient,
  query: EnsureQueryDataOptions<TQueryFnData, TError, TData, TQueryKey>,
): Promise<TData> {
  return queryClient.ensureQueryData(query).catch((failure: unknown) => {
    followIfCompanyChanged(queryClient, failure);
    throw failure;
  });
}

/** As `ensureTenantData`, but a copy older than `staleTime` waits for the server's answer. */
export function fetchTenantData<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
>(
  queryClient: QueryClient,
  query: FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): Promise<TData> {
  return queryClient.fetchQuery(query).catch((failure: unknown) => {
    followIfCompanyChanged(queryClient, failure);
    throw failure;
  });
}

// `href` lets sign-in return the person to where they were going.
export async function requireSignedIn(
  queryClient: QueryClient,
  href: string,
): Promise<SessionSnapshot> {
  const session = await queryClient.ensureQueryData(sessionQuery);
  if (!session.signedIn) {
    throw redirect({ to: "/sign-in", search: { redirect: href } });
  }
  return session;
}

// The slug is a label, never the tenant. An address naming another of this login's companies
// switches the session there, so a shared link opens what it points at; any other slug redirects
// without revealing whether that company exists. A preload never switches.
export async function requireCompany(
  queryClient: QueryClient,
  slug: string,
  href: string,
  preload = false,
): Promise<CompanySession> {
  const company = companyOf(await requireSignedIn(queryClient, href));
  if (!company) throw redirect({ to: "/companies/new" });
  if (company.companySlug === slug) {
    unfinishedOnboarding(company);
    return shown(slug, company, preload);
  }

  const target = preload ? undefined : await ownCompanyAt(queryClient, slug);
  if (!target) {
    throw redirect({ to: "/$slug", params: { slug: company.companySlug } });
  }

  // A failed switch is not an error screen: the session read below says where the person now is.
  await switchCompany(queryClient, target.companyId).catch(() => undefined);
  const moved = companyOf(await requireSignedIn(queryClient, href));
  if (!moved) throw redirect({ to: "/companies/new" });
  unfinishedOnboarding(moved);
  if (moved.companySlug === slug) return shown(slug, moved, preload);
  throw redirect({ to: "/$slug", params: { slug: moved.companySlug } });
}

// A null step means no company yet, never the first step.
export function onboardingPath(
  step: SessionSnapshot["onboardingStep"],
): "/onboarding/profile" | "/onboarding/invite" | null {
  if (step === "profile") return "/onboarding/profile";
  if (step === "invite_team") return "/onboarding/invite";
  return null;
}

// Onboarding is per membership. Checked only once address and session agree, so a link to
// another company still switches there first.
function unfinishedOnboarding(session: SessionSnapshot): void {
  const step = onboardingPath(session.onboardingStep);
  if (step) throw redirect({ to: step });
}

// Onboarding addresses name no company, so the tab vouches for the session's. It never replaces
// one it already vouches for: a tab left behind by a switch is refused and follows, rather than
// saving the step into the other company.
export async function requireOnboarding(
  queryClient: QueryClient,
  href: string,
  preload = false,
): Promise<void> {
  const session = await requireSignedIn(queryClient, href);
  if (!session.companySlug) throw redirect({ to: "/companies/new" });
  if (!preload && shownCompany() === null) showCompany(session.companySlug);
}

// Address and session agree: from here this tab sends `slug` with every tenant request.
function shown(
  slug: string,
  session: CompanySession,
  preload: boolean,
): CompanySession {
  if (!preload) {
    showCompany(slug);
    // A live session again: a refusal from before it, here or on a screen with no shell, is spent.
    signedOutStore.clear();
  }
  return { ...session, companySlug: slug };
}

// Fetched fresh so a company joined in another tab counts. An unreadable list counts as no match,
// which leaves the person where the session already is.
async function ownCompanyAt(queryClient: QueryClient, slug: string) {
  const companies = await queryClient
    .fetchQuery({ ...companiesQuery, staleTime: 0 })
    .catch(() => []);
  return companies.find((company) => company.companySlug === slug);
}
